import { promises as fsp, realpathSync } from 'node:fs';
import * as np from 'node:path';
import type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';
import { PathResolver } from '@livx.cc/wcli/core';
import { isWinPaths, toVfsPath, toHostPath, winSegmentProblem } from './hostPath';

/**
 * Real-disk backend implementing wcli's IFilesystem, rooted at `baseDir`.
 * The VFS path space ('/...') maps under baseDir, so the same agent/tools/commands
 * run unchanged against disk or memory — complete mem↔disk interchangeability.
 * Semantics mirror MemFilesystem (throws on missing parent / existing dir / etc.).
 *
 * Windows (see hostPath.ts): incoming host paths (`C:\…`, backslashes) are translated to VFS form
 * first, and a `baseDir` of '/' is the whole machine — `/c/…` is drive C:, '/' lists the drives.
 * Name aliases that would let a path spell one name and open another (alternate data streams,
 * trailing dots, device names, 8.3 short names) are refused, so name-based policy above this
 * backend (the jail's denylist) sees the name that is actually opened.
 */
export class NodeDiskFilesystem implements IFilesystem {
  private cwd = '/';
  private opts: { denySymlinks: boolean };
  private readonly win = isWinPaths();
  /** Windows + baseDir '/': the VFS root is the machine (drives as first segment), not a directory. */
  private readonly drives: boolean;

  constructor(private baseDir: string, opts: { denySymlinks?: boolean } = {}) {
    // denySymlinks (default ON): refuse to traverse a symlink anywhere in the path, so
    // a symlink planted inside the root cannot escape it (read/write/delete a target outside).
    // On Windows a directory junction is a reparse point that lstat reports as a symlink — same refusal.
    this.opts = { denySymlinks: true, ...opts };
    this.drives = this.win && (baseDir === '/' || baseDir === '\\');
  }

  /** Ensure the root dir exists. */
  async init(): Promise<void> {
    if (this.drives) return; // the machine root: nothing to create
    await fsp.mkdir(this.baseDir, { recursive: true });
  }

  private real(vpath: string): string {
    if (!this.win) return np.join(this.baseDir, '.' + vpath); // vpath is absolute in VFS space
    for (const seg of vpath.split('/')) {
      const why = seg && winSegmentProblem(seg);
      if (why) throw new Error(`File not found: ${vpath} (${why})`);
    }
    if (!this.drives) return np.join(this.baseDir, '.' + vpath);
    const host = toHostPath(vpath);
    if (!host) throw new Error(`File not found: ${vpath}`);
    return host;
  }

  /** Windows machine root ('/' when baseDir is '/'): a synthetic directory listing the drives. */
  private isDriveRoot(vpath: string): boolean { return this.drives && vpath === '/'; }

  /** Windows: refuse an 8.3 short name (`ENV~1` opens `.env`) — the only alias that needs the disk to
   *  detect. Only segments that look like one (`~<digit>`) cost a lookup: the OS's long name for that
   *  component must be the name that was asked for. */
  private assertLongNames(real: string): void {
    if (!this.win || !/~\d/.test(real)) return;
    const root = np.parse(real).root;
    const parts = real.slice(root.length).split(/[\\/]/).filter(Boolean);
    let cur = root;
    for (const part of parts) {
      cur = np.join(cur, part);
      if (!/~\d/.test(part)) continue;
      let long: string;
      try { long = np.basename(realpathSync.native(cur)); } catch { return; } // doesn't exist (yet) — nothing aliased
      if (long.toLowerCase() !== part.toLowerCase()) throw new Error('File not found: short (8.3) name not permitted');
    }
  }

  // Verified non-symlink DIRECTORY components, with a short TTL: tree walks (Glob/Grep) hit the same
  // parents thousands of times; re-lstat'ing each per op is the dominant syscall cost. The 1s window
  // is an accepted race only against an out-of-band symlink swap mid-walk (this FS can't create
  // symlinks itself); leaf components are never cached.
  private verified = new Map<string, number>();
  private static VERIFY_TTL_MS = 1000;

  /** Throw if any existing component of `real` is a symlink (escape vector). */
  private async assertNoSymlink(real: string): Promise<void> {
    this.assertLongNames(real);
    if (!this.opts.denySymlinks) return;
    const base = this.drives ? np.parse(real).root : this.baseDir; // Windows machine root: walk from the drive
    const rel = np.relative(base, real);
    if (rel === '' || rel.startsWith('..') || np.isAbsolute(rel)) return; // root itself / outside (can't happen post-join)
    const parts = rel.split(np.sep);
    let cur = base;
    const now = Date.now();
    for (let i = 0; i < parts.length; i++) {
      cur = np.join(cur, parts[i]);
      const isLeaf = i === parts.length - 1;
      if (!isLeaf && (this.verified.get(cur) ?? 0) > now) continue;
      let st;
      try { st = await fsp.lstat(cur); } catch { return; } // component doesn't exist yet (e.g. new file) — nothing further to check
      if (st.isSymbolicLink()) throw new Error('File not found: symlink not permitted');
      if (!isLeaf) {
        if (this.verified.size > 10_000) this.verified.clear(); // bound memory on huge trees
        this.verified.set(cur, now + NodeDiskFilesystem.VERIFY_TTL_MS);
      }
    }
  }

  resolvePath(path: string, cwd?: string): string {
    return PathResolver.resolve(toVfsPath(path), toVfsPath(cwd || this.cwd));
  }
  getCwd(): string { return this.cwd; }
  setCwd(path: string): void { this.cwd = PathResolver.normalize(toVfsPath(path)); }

  async readFile(path: string): Promise<string> {
    const r = this.real(this.resolvePath(path));
    try {
      await this.assertNoSymlink(r);
      const st = await fsp.stat(r);
      if (st.isDirectory()) throw new Error(`Not a file: ${path}`);
      return await fsp.readFile(r, 'utf8');
    } catch (e) {
      if (e instanceof Error && /Not a file/.test(e.message)) throw e;
      throw new Error(`File not found: ${path}`);
    }
  }

  /** The REAL on-disk path behind a VFS path — an optional capability (not on IFilesystem) the Read
   *  tool duck-types so it can persist an image REFERENCE instead of megabytes of base64. */
  realPath(path: string): string { return this.real(this.resolvePath(path)); }

  /** The CANONICAL on-disk path — symlinks resolved. `realPath` is path arithmetic (baseDir + vpath)
   *  and therefore reports a symlink and its target as different places, which is no use as an
   *  identity: a tree walk that dedupes on it still re-enters a loop (`a/b/loop -> a` re-walked 11
   *  times before a depth cap stopped it). Optional capability, duck-typed by the file-scanning
   *  tools; falls back to the arithmetic path for anything unresolvable (a broken/racing link). */
  canonicalPath(path: string): string {
    const p = this.realPath(path);
    try { return realpathSync(p); } catch { return p; }
  }

  /** Read raw bytes (for binary files like images). Not on the base IFilesystem (which is utf8-only) — an
   *  optional capability the Read tool duck-types to return image blocks. Same symlink/jail guards as readFile. */
  async readFileBytes(path: string): Promise<Uint8Array> {
    const r = this.real(this.resolvePath(path));
    try {
      await this.assertNoSymlink(r);
      const st = await fsp.stat(r);
      if (st.isDirectory()) throw new Error(`Not a file: ${path}`);
      return await fsp.readFile(r); // no encoding → Buffer (Uint8Array)
    } catch (e) {
      if (e instanceof Error && /Not a file/.test(e.message)) throw e;
      throw new Error(`File not found: ${path}`);
    }
  }

  async writeFile(path: string, content: string): Promise<void> {
    const r = this.real(this.resolvePath(path));
    await this.assertNoSymlink(r);
    const parent = np.dirname(r);
    try {
      if (!(await fsp.stat(parent)).isDirectory()) throw 0;
    } catch {
      throw new Error(`Parent directory does not exist: ${path}`);
    }
    await fsp.writeFile(r, content, 'utf8');
  }

  async deleteFile(path: string): Promise<void> {
    const r = this.real(this.resolvePath(path));
    await this.assertNoSymlink(r);
    let st;
    try { st = await fsp.stat(r); } catch { throw new Error(`File not found: ${path}`); }
    if (st.isDirectory()) {
      if ((await fsp.readdir(r)).length > 0) throw new Error(`Directory not empty: ${path}`);
      await fsp.rmdir(r);
    } else {
      await fsp.unlink(r);
    }
  }

  async readDir(path: string): Promise<string[]> {
    const v = this.resolvePath(path);
    if (this.isDriveRoot(v)) return this.listDrives();
    try { const r = this.real(v); await this.assertNoSymlink(r); return await fsp.readdir(r); }
    catch { throw new Error(`Directory not found: ${path}`); }
  }

  async createDir(path: string): Promise<void> {
    if (await this.exists(path)) throw new Error(`File or directory already exists: ${path}`);
    const r = this.real(this.resolvePath(path));
    await this.assertNoSymlink(r);
    const parent = np.dirname(r);
    try {
      if (!(await fsp.stat(parent)).isDirectory()) throw 0;
    } catch {
      throw new Error(`Parent directory does not exist: ${path}`);
    }
    await fsp.mkdir(r);
  }

  async exists(path: string): Promise<boolean> {
    const v = this.resolvePath(path);
    if (this.isDriveRoot(v)) return true;
    try { const r = this.real(v); await this.assertNoSymlink(r); await fsp.stat(r); return true; } catch { return false; }
  }

  async stat(path: string): Promise<FileMetadata> {
    let s;
    const v = this.resolvePath(path);
    if (this.isDriveRoot(v)) return { created: new Date(0), modified: new Date(0), size: 0, permissions: 'drwxr-xr-x', isExecutable: false };
    try { const r = this.real(v); await this.assertNoSymlink(r); s = await fsp.stat(r); }
    catch { throw new Error(`File not found: ${path}`); }
    return {
      created: s.birthtime,
      modified: s.mtime,
      size: s.size,
      permissions: s.isDirectory() ? 'drwxr-xr-x' : '-rw-r--r--',
      isExecutable: false,
    };
  }

  async isDirectory(path: string): Promise<boolean> {
    const v = this.resolvePath(path);
    if (this.isDriveRoot(v)) return true;
    try { const r = this.real(v); await this.assertNoSymlink(r); return (await fsp.stat(r)).isDirectory(); } catch { return false; }
  }
  async isFile(path: string): Promise<boolean> {
    try { const r = this.real(this.resolvePath(path)); await this.assertNoSymlink(r); return (await fsp.stat(r)).isFile(); } catch { return false; }
  }

  /** Windows machine root: the drive letters that are mounted (`/c`, `/d`, …). */
  private async listDrives(): Promise<string[]> {
    const letters = 'abcdefghijklmnopqrstuvwxyz'.split('');
    const up = await Promise.all(letters.map((l) => fsp.stat(`${l.toUpperCase()}:\\`).then(() => true, () => false)));
    return letters.filter((_, i) => up[i]);
  }
}
